const DAYS = [
    { value: 'mon', label: 'Mon', name: 'Monday' },
    { value: 'tue', label: 'Tue', name: 'Tuesday' },
    { value: 'wed', label: 'Wed', name: 'Wednesday' },
    { value: 'thu', label: 'Thu', name: 'Thursday' },
    { value: 'fri', label: 'Fri', name: 'Friday' },
    { value: 'sat', label: 'Sat', name: 'Saturday' },
    { value: 'sun', label: 'Sun', name: 'Sunday' },
] as const;

export type Day = typeof DAYS[number]['value'];

function parseDays(value: string): Day[] {
    const values = value.toLowerCase().split(/[\s,]+/).filter(Boolean);
    return values.map(value => {
        const day = DAYS.find(day => day.value === value);
        if (!day) throw new RangeError(`Invalid day-selector day: "${value}". Use mon, tue, wed, thu, fri, sat or sun.`);
        return day.value;
    });
}

export class DaySelector extends HTMLElement {
    static formAssociated = true;
    static observedAttributes = [
        'selected', 'disabled-days', 'disabled', 'open',
        ...DAYS.map(day => `disabled-${day.value}`),
    ];

    #internals: ElementInternals;
    #selected = new Set<Day>(DAYS.map(day => day.value));
    #formDisabled = false;
    #toggle: HTMLButtonElement;
    #summary: HTMLSpanElement;
    #panel: HTMLDivElement;
    #selectAll: HTMLButtonElement;
    #done: HTMLButtonElement;
    #inputs: HTMLInputElement[];

    constructor() {
        super();
        const template = document.getElementById('day-selector-template');
        if (!(template instanceof HTMLTemplateElement)) {
            throw new Error('day-selector-template not found');
        }
        this.#internals = this.attachInternals();
        const root = this.attachShadow({ mode: 'open' });
        root.appendChild(template.content.cloneNode(true));
        this.#toggle = root.querySelector<HTMLButtonElement>('.toggle')!;
        this.#summary = root.querySelector<HTMLSpanElement>('.summary')!;
        this.#panel = root.querySelector<HTMLDivElement>('.panel')!;
        this.#selectAll = root.querySelector<HTMLButtonElement>('.select-all')!;
        this.#done = root.querySelector<HTMLButtonElement>('.done')!;
        this.#inputs = DAYS.map(day => {
            const input = root.querySelector<HTMLInputElement>(`input[value="${day.value}"]`);
            if (!input) throw new Error(`day-selector-template is missing the ${day.value} checkbox`);
            return input;
        });

        this.#toggle.addEventListener('click', () => { this.open = !this.open; });
        this.#done.addEventListener('click', () => this.#collapse());
        this.#inputs.forEach((input, index) => {
            const label = root.querySelector<HTMLLabelElement>(`label[for="${input.id}"]`)!;
            input.addEventListener('click', event => {
                if (!input.disabled && !input.checked && this.#selected.has(DAYS[index].value) && this.#selected.size === 1) {
                    event.preventDefault();
                    input.classList.add('last-checkbox');
                    label.classList.add('last-checkbox');
                }
            });
            label.addEventListener('animationend', () => {
                input.classList.remove('last-checkbox');
                label.classList.remove('last-checkbox');
            });
            input.addEventListener('input', event => event.stopPropagation());
            input.addEventListener('change', event => {
                event.stopPropagation();
                if (input.disabled) return;
                const day = DAYS[index].value;
                if (input.checked) this.#selected.add(day);
                else this.#selected.delete(day);
                this.#update();
                this.#notify();
            });
        });
        this.#selectAll.addEventListener('click', () => {
            if (this.#selectAll.disabled) return;
            this.#selected = new Set(DAYS.map(day => day.value).filter(day => !this.disabledDays.includes(day)));
            this.#update();
            this.#notify();
        });
    }

    connectedCallback(): void {
        this.#update();
    }

    attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
        if (oldValue === newValue) return;
        if (name === 'selected') {
            this.#selected = new Set(newValue === null ? DAYS.map(day => day.value) : parseDays(newValue));
        }
        this.#update();
    }

    get selectedDays(): Day[] {
        return DAYS.map(day => day.value).filter(day => this.#selected.has(day));
    }

    set selectedDays(days: Day[]) {
        this.#selected = new Set(parseDays(days.join(',')));
        this.#update();
    }

    get disabledDays(): Day[] {
        const disabled = new Set(parseDays(this.getAttribute('disabled-days') ?? ''));
        return DAYS.map(day => day.value).filter(day => disabled.has(day) || this.hasAttribute(`disabled-${day}`));
    }

    get summary(): string {
        if (this.#selected.size === DAYS.length) return 'All days';
        return DAYS.filter(day => this.#selected.has(day.value)).map(day => day.label).join(', ') || 'No days';
    }

    get open(): boolean {
        return this.hasAttribute('open');
    }

    set open(value: boolean) {
        this.toggleAttribute('open', value);
    }

    formDisabledCallback(disabled: boolean): void {
        this.#formDisabled = disabled;
        this.#update();
    }

    formResetCallback(): void {
        const initial = this.getAttribute('selected');
        this.#selected = new Set(initial === null ? DAYS.map(day => day.value) : parseDays(initial));
        this.#update();
    }

    formStateRestoreCallback(state: string | File | FormData): void {
        if (typeof state !== 'string') throw new TypeError('day-selector form state must be a string.');
        this.selectedDays = parseDays(state);
    }

    #collapse(): void {
        this.open = false;
        this.#toggle.focus();
    }

    #notify(): void {
        this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
        this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    }

    #update(): void {
        const disabledDays = this.disabledDays;
        disabledDays.forEach(day => this.#selected.delete(day));
        const disabled = this.#formDisabled || this.hasAttribute('disabled');
        this.#inputs.forEach((input, index) => {
            const day = DAYS[index].value;
            input.classList.remove('last-checkbox');
            input.labels?.forEach(label => label.classList.remove('last-checkbox'));
            input.disabled = disabled || disabledDays.includes(day);
            input.checked = this.#selected.has(day);
        });
        this.#summary.textContent = this.summary;
        this.#toggle.setAttribute('aria-expanded', String(this.open));
        this.#toggle.disabled = disabled;
        this.#panel.hidden = !this.open;
        this.#done.disabled = disabled;
        this.#selectAll.disabled = disabled || DAYS.every(day => disabledDays.includes(day.value) || this.#selected.has(day.value));
        const value = this.selectedDays.join(',');
        this.#internals.setFormValue(value, value);
    }
}

customElements.define('day-selector', DaySelector);

declare global {
    interface HTMLElementTagNameMap {
        'day-selector': DaySelector;
    }
}
